import { ActivatedRoute } from '@angular/router';
import { AuthenticationFacade, LoginError, LoginWithPasswordError } from '../authentication';
import { BaseError, UserActivationMissingError } from '../error-classifier';
import { ConfigSettingsService, LogService, MessageBase } from '../providers';
import { Directive, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Directive()
export abstract class AuthHandler implements OnInit, OnDestroy {
  public isExternalLoginEnabled = signal<boolean>(false);
  protected destroyed$ = new Subject<boolean>();

  protected constructor(
    protected readonly auth: AuthenticationFacade,
    private config: ConfigSettingsService,
    private messageService: MessageBase,
    protected readonly route?: ActivatedRoute,
  ) {}

  public classifyError(error: LoginWithPasswordError): BaseError {
    if (error.error && error.error === 'server.error.auth.user_activation_missing') {
      return new UserActivationMissingError(this.messageService);
    }

    return new BaseError(this.messageService, error.error);
  }

  public async loginWithCodeFlow(): Promise<void> {
    await this.auth.authenticate();
    this.onCodeLogin();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public ngOnInit(): void {
    this.isExternalLoginEnabled.set(this.config.appSettings.supportedOAuthGrants.has('code'));

    if (!this.isExternalLoginEnabled()) {
      return;
    }

    this.route?.queryParams
      .pipe(
        takeUntil(this.destroyed$),
        filter((params) => 'error' in params),
      )
      .subscribe(async ({ error, error_description }) => {
        if (error) {
          const loginError = new LoginError(error_description ?? 'general.text.error', error);
          LogService.error(`${loginError.name}: ${loginError.message}`);
          await this.handleCodeLoginError(loginError);
        }
      });
  }

  /**
   * Callback that is called if a login by oauth code flow fails
   */
  protected abstract handleCodeLoginError(error: LoginError): Promise<void>;

  /**
   * Callback that is called if a login with password attempt fails
   */
  protected abstract handleLoginWithPasswordError(
    error: LoginWithPasswordError,
    formModel: { username: string; password: string },
  ): void;

  /**
   * Callback that is called once an oauth code login succeeds
   */
  protected abstract onCodeLogin(): void;

  /**
   * Callback that is called once any loading logic should be disabled
   * @param handler the handler obtained from the {@link AuthHandler#onLoadingStart} call
   */
  protected abstract onLoadingEnd(): Promise<void>;

  /**
   * Callback that is called once some kind of loading logic should be started
   * @returns a handler that is passed to the {@link AuthHandler#onLoadingEnd} callback later
   */
  protected abstract onLoadingStart(): Promise<void>;

  protected async loginWithPassword(username: string, password: string): Promise<boolean> {
    await this.onLoadingStart();

    username = username.trim();

    try {
      await this.auth.authenticate(username, password);

      return true;
    } catch (e: unknown) {
      const error = e as unknown as LoginWithPasswordError;
      if (error.error && error.error_description) {
        this.handleLoginWithPasswordError(error, {
          username,
          password,
        });
        return false;
      }
    } finally {
      await this.onLoadingEnd();
    }
    return false;
  }
}
